import { useState, useEffect } from 'react';
import { localePath } from '../config/region';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Pencil, Trash2, Play, BookOpen, Save, X } from 'lucide-react';
import { API_URLS } from '../config/api';
import { apiGet, apiPut, apiPost, apiDelete } from '../utils/apiUtils';
import { getNovelCoverUrl } from '../utils/novelUtils';
import { t } from '../i18n';
import ParticleBackground from './ParticleBackground';
import SeoHead from './SeoHead';
import './LibraryNovelDetail.css';

// Same cap as the custom biography in PlayPage and USER_BIOGRAPHY_LIMIT on the backend.
const BIOGRAPHY_MAX = 2500;
const NAME_MAX = 100;

const LibraryNovelDetail = () => {
  const { playLink } = useParams();
  const navigate = useNavigate();

  const [novel, setNovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [promptName, setPromptName] = useState('');
  const [promptBiography, setPromptBiography] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const [savesOpen, setSavesOpen] = useState(false);
  const [saves, setSaves] = useState([]);
  const [savesLoading, setSavesLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet(API_URLS.LIBRARY_NOVEL(playLink), {}, false, '', true);
        setNovel(res.data);
        setPromptName(res.data.user_biography_name || '');
        setPromptBiography(res.data.user_biography || '');
      } catch {
        setError(t('Ошибка при загрузке новеллы'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [playLink]);

  const handleUpdateBiography = async () => {
    try {
      await apiPut(
        API_URLS.USER_NOVEL_GAME_BIOGRAPHY(novel.novel_id),
        { prompt_name: promptName, prompt_biography: promptBiography },
        {},
        true,
        t('Биография успешно обновлена')
      );
      setNovel({ ...novel, user_biography_name: promptName, user_biography: promptBiography });
      setIsEditing(false);
    } catch {
      // error handled by apiPut
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('Удалить новеллу из библиотеки?'))) return;
    try {
      await apiDelete(API_URLS.REMOVE_FROM_LIBRARY(playLink), {}, true, t('Новелла удалена из библиотеки'));
      navigate('/library');
    } catch {
      // error handled by apiDelete
    }
  };

  const openSaves = async () => {
    setSavesOpen(true);
    setSavesLoading(true);
    try {
      const res = await apiGet(API_URLS.GAME_SAVES(novel.novel_id));
      setSaves(res.data);
    } catch {
      setSaves([]);
    } finally {
      setSavesLoading(false);
    }
  };

  const handleLoadSave = async (gameStateId) => {
    setSavesOpen(false);
    try {
      await apiGet(API_URLS.GAME_HISTORY_ROLLBACK(novel.novel_id, gameStateId));
      navigate(`/play/${playLink}`);
    } catch {
      // error handled
    }
  };

  const handleDeleteSave = async (saveId) => {
    setSavesLoading(true);
    try {
      await apiDelete(API_URLS.GAME_DELETE_SAVE(novel.novel_id, saveId));
      const res = await apiGet(API_URLS.GAME_SAVES(novel.novel_id));
      setSaves(res.data);
    } catch {
      // error handled
    } finally {
      setSavesLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="lnd-wrap">
        <ParticleBackground />
        <div className="lnd-app">
          <div className="lnd-loading">
            <div className="lnd-spinner" />
            <span>{t('Загрузка…')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !novel) {
    return (
      <div className="lnd-wrap">
        <ParticleBackground />
        <div className="lnd-app">
          <div className="lnd-error">
            <span>{error || t('Новелла не найдена')}</span>
            <a className="lnd-btn-secondary" href={localePath('/library')} style={{ textDecoration: 'none' }}>
              <ChevronLeft size={14} /> {t('В библиотеку')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  const coverUrl = getNovelCoverUrl(novel, novel.novel_id);

  const handleNewGame = async () => {
    if (!window.confirm(t('Начать новую игру? Текущий прогресс будет сброшен.'))) return;
    await apiPost(API_URLS.GAME_NEW(playLink), {}, {}, true);
    navigate(`/play/${playLink}`);
  };

  const novelTitle = novel.name ? `${novel.name} — AI Novel` : 'AI Novel';
  const novelDescription = novel.description || t('Играй в интерактивную новеллу «{0}» с персонажами на основе искусственного интеллекта.', novel.name);

  return (
    <div className="lnd-wrap">
      {/* A signed-in reader's own saved game — nothing to index, and it duplicates the public
          /production page for the same novel. */}
      <SeoHead
        title={novelTitle}
        description={novelDescription}
        image={novel.cover_background_image_id ? coverUrl : undefined}
        imageAlt={novel.cover_background_image_id ? novel.name : undefined}
        noindex
      />
      <ParticleBackground />

      <div className="lnd-app">

        <div className="lnd-header">
          <a className="lnd-back" href={localePath('/library')}>
            <ChevronLeft size={14} /> {t('Библиотека')}
          </a>
          <button className="lnd-btn-danger" onClick={handleDelete}>
            <Trash2 size={13} /> {t('Удалить')}
          </button>
        </div>

        <div className="lnd-content">

          <div className="lnd-cover">
            <img src={coverUrl} alt={novel.name} />
          </div>

          <div className="lnd-info">
            <h1 className="lnd-title">{novel.name}</h1>
            {novel.description && <p className="lnd-desc">{novel.description}</p>}

            {/* Biography */}
            <div className="lnd-bio">
              <h3 className="lnd-bio-title">{t('Биография персонажа')}</h3>

              {isEditing ? (
                <>
                  <div className="lnd-form-group">
                    <label className="lnd-form-label">{t('Имя персонажа')}</label>
                    <input
                      className="lnd-form-input"
                      type="text"
                      value={promptName}
                      onChange={(e) => setPromptName(e.target.value.slice(0, NAME_MAX))}
                      placeholder={t('Введите имя…')}
                    />
                  </div>
                  <div className="lnd-form-group">
                    <label className="lnd-form-label">{t('Биография')}</label>
                    <textarea
                      className="lnd-form-textarea"
                      value={promptBiography}
                      onChange={(e) => setPromptBiography(e.target.value.slice(0, BIOGRAPHY_MAX))}
                      rows={5}
                      placeholder={t('Расскажите о своём персонаже…')}
                    />
                    <div className={`lnd-bio-char-count${promptBiography.length > BIOGRAPHY_MAX - 125 ? ' warn' : ''}`}>
                      {promptBiography.length}/{BIOGRAPHY_MAX}
                    </div>
                  </div>
                  <div className="lnd-form-actions">
                    <button className="lnd-btn-primary" onClick={handleUpdateBiography}>
                      {t('Сохранить')}
                    </button>
                    <button className="lnd-btn-cancel" onClick={() => setIsEditing(false)}>
                      {t('Отмена')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="lnd-bio-row">
                    <span className="lnd-bio-label">{t('Имя')}</span>
                    {novel.user_biography_name
                      ? <span className="lnd-bio-value">{novel.user_biography_name}</span>
                      : <span className="lnd-bio-empty">{t('Не задано')}</span>}
                  </div>
                  <div className="lnd-bio-row">
                    <span className="lnd-bio-label">{t('Биография')}</span>
                    {novel.user_biography
                      ? <span className="lnd-bio-value">{novel.user_biography}</span>
                      : <span className="lnd-bio-empty">{t('Не задана')}</span>}
                  </div>
                  <button className="lnd-bio-edit-btn" onClick={() => setIsEditing(true)}>
                    <Pencil size={12} /> {t('Редактировать')}
                  </button>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="lnd-actions">
              <a className="lnd-btn-primary" href={`/play/${playLink}`} style={{ textDecoration: 'none' }}>
                <Play size={13} fill="currentColor" /> {t('Продолжить')}
              </a>
              <button className="lnd-btn-secondary" onClick={openSaves}>
                <Save size={13} /> {t('Сохранения')}
              </button>
              <button className="lnd-btn-secondary" onClick={handleNewGame}>
                <BookOpen size={13} /> {t('Новая игра')}
              </button>
            </div>
          </div>
        </div>

        <div className="lnd-spacer" />
      </div>

      {/* Saves modal */}
      {savesOpen && (
        <div className="lnd-modal-overlay" onClick={() => setSavesOpen(false)}>
          <div className="lnd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lnd-modal-head">
              <h3 className="lnd-modal-title">{t('Сохранения')}</h3>
              <button className="lnd-modal-close" onClick={() => setSavesOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="lnd-modal-body">
              {savesLoading ? (
                <div className="lnd-modal-loading">
                  <div className="lnd-spinner" />
                </div>
              ) : saves.length === 0 ? (
                <div className="lnd-modal-empty">{t('Сохранений не найдено')}</div>
              ) : saves.map((save) => (
                <div key={save.save_id} className="lnd-save-item">
                  <span className="lnd-save-name">{save.name}</span>
                  <div className="lnd-save-btns">
                    <button className="lnd-save-load" onClick={() => handleLoadSave(save.game_state_id)}>
                      {t('Загрузить')}
                    </button>
                    <button className="lnd-save-delete" onClick={() => handleDeleteSave(save.save_id)}>
                      {t('Удалить')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryNovelDetail;
